import { IRuleTableDefinition } from "./IRuleTableDefinition";
import { ResultCell } from "./ResultCell";
import { Executable } from "../../../Common/Execution/Executable";
import { DataDefinition } from "../../../Common/Data/DataDefinition";
import { DataUtilities } from "../../../Common/DataUtilities";
import { IDataDefinition } from "../../../Common/Data/Definitions/IDataDefinition";
import { BooleanDataDefinition } from "../../../Common/Data/Definitions/BooleanDataDefinition";
import { ValidationIssue } from "../../../Common/IDesignValidationProvider";
import { ObjectDataDefinition } from "../../../Common/Data/Definitions/ObjectDataDefinition";
import { DataTypes } from "../../../Common/Data/DataTypes";
import { TableColumn } from "./TableColumn";

export abstract class BaseRuleTableDefinition extends Executable implements IRuleTableDefinition {
    protected _resultColumns: TableColumn[] = [];

    get resultColumns() {
        return this._resultColumns;
    }

    protected _defaultResult: { [id: string]: ResultCell } = {};

    get defaultResult(): { [id: string]: ResultCell } {
        return this._defaultResult;
    }

    protected _allowDefaultResults: boolean = true;

    get allowDefaultResults(): boolean {
        return this._allowDefaultResults;
    }

    set allowDefaultResults(allowDefaultResults: boolean) {
        this._allowDefaultResults = allowDefaultResults;
    }

    get executionResult() {
        return this._executionResult;
    }

    set executionResult(res: (() => DataDefinition) | DataDefinition | undefined) {
        let executionResult: DataDefinition | undefined;
        if (res instanceof Function) {
            executionResult = res();
        } else {
            executionResult = res;
        }

        // initialize
        this._resultColumns = [];
        this._defaultResult = {};

        if (!executionResult) {
            // no need this code?
            this.addResultColumn();
        } else {
            for (let field of executionResult.fields) {
                let rescol = this.addResultColumnInternal(field.name);
                rescol.dataType = field.type;
            }

            if (executionResult.fields.length === 0 && res instanceof DataDefinition) {
                let rescol = this.addResultColumnInternal("result1");
                // update the output;
                res.fields.push(DataUtilities.createDataByType(rescol.dataType!, rescol.name));
            }
        }

        this.syncDefaultResults();
        this.syncAllRows();

        this._executionResult = res;
    }

    syncAllRows() {
        throw new Error("not implemented");
    }

    syncDefaultResults() {
        throw new Error("not implemented");
    }

    addResultColumn(name?: string): TableColumn {
        if (!this.canChangeOutputs) {
            throw new Error("cannot add result columns because the outputs are specified by API");
        }

        let rescol = this.addResultColumnInternal(name);
        // update the output;
        this.getExecutionResult().fields.push(DataUtilities.createDataByType(rescol.dataType!, rescol.name));

        this.syncDefaultResults();
        this.syncAllRows();
        return rescol;
    }

    removeResultColumn(id: string) {
        if (!this.canChangeOutputs) {
            throw new Error("cannot add result columns because the outputs are specified by API");
        }

        if (this._resultColumns.length > 1) {
            let resultColIdx = this._resultColumns.findIndex((resultCol) => resultCol._id === id);
            if (resultColIdx < 0) return;

            let resultcol = this._resultColumns[resultColIdx];
            this._resultColumns.splice(resultColIdx, 1);

            this.getExecutionResult().fields = this.getExecutionResult().fields.filter(
                (f) => f.name !== resultcol!.name || f.type !== resultcol!.dataType
            );

            this.syncDefaultResults();
            this.syncAllRows();
        }
    }

    getDefaultResultByColumnId(columnId: string): ResultCell | undefined {
        return this.defaultResult[columnId];
    }

    getResultColumnType(resultcol: TableColumn): IDataDefinition | undefined {
        return this.getExecutionResult().fields.find((f) => f.name === resultcol.name && f.type === resultcol.dataType);
    }

    setResultColumnType(rule: TableColumn, dataType: IDataDefinition): void {
        if (!this.canChangeOutputs) throw new Error("it's not editable");

        let defaultResult = this.getDefaultResultByColumnId(rule._id);

        // not sure if it's needed
        if (defaultResult) {
            if (
                dataType instanceof BooleanDataDefinition &&
                (defaultResult.value === undefined || defaultResult.value === null)
            ) {
                defaultResult.value = false;
            }
            defaultResult.value = dataType.convert(defaultResult.value);
        }

        let outputdef = this.getExecutionResult();

        // remove old and add new;
        outputdef.fields = outputdef.fields.map((f) => {
            if (f.name === rule.name && f.type === rule.dataType) {
                return dataType;
            }
            return f;
        });

        rule.name = dataType.name;
        rule.dataType = dataType.type;
    }

    getResultColumnValidationIssues(rescol: TableColumn): ValidationIssue[] {
        let issues = rescol.getDesignValidationIssues();

        if (!rescol.dataType) {
            issues.push(new ValidationIssue("datatype is not set"));
        }

        if (this.resultColumns.find((resC) => resC._id !== rescol._id && resC.name === rescol.name)) {
            issues.push(new ValidationIssue("duplicate names"));
        }

        let datadef = this.getResultColumnType(rescol);

        if (!datadef) {
            issues.push(new ValidationIssue("data path is incorrect"));
        } else if (datadef instanceof ObjectDataDefinition) {
            if (datadef.fields.length === 0) {
                issues.push(new ValidationIssue("fields are not configured"));
            }
        }

        return issues;
    }

    getResultCellValidationIssues(cell: ResultCell): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        let resultcol = this.resultColumns.find((rescol) => rescol._id === cell._columnId);

        if (!resultcol) issues.push(new ValidationIssue("invalid column id"));
        else {
            const resultdatadef = this.getResultColumnType(resultcol);

            if (!resultdatadef) {
                issues.push(new ValidationIssue(`resultcol[${resultcol.name}] is invalid.`));
            } else {
                const valueerror = resultdatadef.verifyValueType(cell.value);
                if (valueerror) issues.push(valueerror);
            }

            issues.push(...cell.validateCellData(DataUtilities.createDataByType(resultcol.dataType!, resultcol.name)));
        }

        return issues;
    }

    getDefaultResultCellValidationIssues(resultcell: ResultCell): ValidationIssue[] {
        let resultcol = this.resultColumns.find((resc) => resc._id === resultcell._columnId);

        let issues = resultcell.validateCellData(DataUtilities.createDataByType(resultcol?.dataType!, resultcol?.name));

        return issues;
    }

    protected getResultColumnByName(name: string): TableColumn | undefined {
        return this.resultColumns.find((c) => c.name === name);
    }

    protected addResultColumnInternal(name?: string): TableColumn {
        const newresultcol = new TableColumn();

        if (!name) {
            let index = this.resultColumns.length;

            do {
                index = index + 1;
                name = "result_" + index;
            } while (this.getResultColumnByName(name) !== undefined);
        }

        newresultcol.name = name;
        newresultcol.dataType = DataTypes.STRING;

        this._resultColumns.push(newresultcol);

        return newresultcol;
    }
}
